
import React from "react";
import styled from "styled-components";
import { CardHeaderTag } from "components/shared/Tags/StatusTags";

// ---------------------------------------------------------------------------
// Internal styled components
// ---------------------------------------------------------------------------

const CardRoot = styled.article`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e8ebf1;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(34, 45, 65, 0.05);
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
`;

/**
 * Flex-wrap row of pill/tag elements shown at the top of the card.
 * Mirrors GrnCardTopTags from styles.grn.js.
 */
const CardHeaderTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  padding-left: 16px;
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px 16px 8px;
  gap: 12px;
`;

const CardHeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const CardHeading = styled.h2`
  margin: 0;
  color: #293142;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardSubHeading = styled.p`
  margin: 4px 0 0;
  color: #7b8498;
  font-size: 12px;
  font-weight: 400;
`;

const CardTopRight = styled.div`
  flex-shrink: 0;
`;

const CardInner = styled.div`
  padding: 0 0 4px;
`;

/**
 * Footer strip at the bottom of the card.
 * Mirrors GrnCardStatusFooter from styles.grn.js.
 */
const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  min-height: 34px;
  gap: 16px;
  padding: 10px 16px 10px 20px;
  border-bottom-right-radius: 12px;
  border-bottom-left-radius: 16px;
  border: 2px solid var(--color-base-white, rgba(255, 255, 255, 1));
  background: ${(props) => `linear-gradient(90deg, ${props.$bg || "#787878"} 0%, #ffffff 100%)`};
  color: ${(props) => props.$text_color || "#494f58"};
  margin-top: auto;

  span {
    color: inherit;
  }
`;

// ---------------------------------------------------------------------------
// OrderCard component
// ---------------------------------------------------------------------------

/**
 * Generic, reusable order card shared between PurchaseOrderCard and GrnCard.
 *
 * @param {Array<{text: string, text_color: string, bg: string}>} headerTags
 *   Array of tag objects rendered as pill elements in the header row.
 *   Only tags with a truthy `text` field are rendered.
 * @param {string}      headingText       Main heading inside the card body
 * @param {string}      subHeadingText    Sub-heading inside the card body
 * @param {function}    cardOnClick       Click handler for the entire card
 * @param {ReactNode}   rightTopDiv       Slot rendered top-right (e.g. a chevron / action button)
 * @param {ReactNode}   InnerCardContent  Slot for inner card body content (MetaPanel / CardWrapper)
 * @param {ReactNode}   footer            Content rendered inside the footer strip
 * @param {string}      footerBg          Background colour for the footer gradient start
 * @param {string}      footerTextColor   Text colour for the footer strip
 */
export const OrderCard = ({
  headerTags,
  headingText,
  subHeadingText,
  cardOnClick,
  rightTopDiv,
  InnerCardContent,
  footer,
  footerBg,
  footerTextColor,
}) => {
  const handleKeyDown = (event) => {
    if (cardOnClick && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      cardOnClick(event);
    }
  };

  const visibleTags = (headerTags || []).filter((tag) => tag?.text);

  return (
    <CardRoot
      onClick={cardOnClick}
      onKeyDown={handleKeyDown}
      role={cardOnClick ? "button" : undefined}
      tabIndex={cardOnClick ? 0 : undefined}
    >
      {visibleTags.length > 0 && (
        <CardHeaderTagsRow>
          {visibleTags.map((tag, index) => (
            <CardHeaderTag
              key={`${tag.text}-${index}`}
              text={tag.text}
              bgColor={tag.bg}
              textColor={tag.text_color}
            />
          ))}
        </CardHeaderTagsRow>
      )}

      <CardTop>
        <CardHeadingGroup>
          {headingText && <CardHeading>{headingText}</CardHeading>}
          {subHeadingText && <CardSubHeading>{subHeadingText}</CardSubHeading>}
        </CardHeadingGroup>

        {rightTopDiv && <CardTopRight>{rightTopDiv}</CardTopRight>}
      </CardTop>

      {InnerCardContent && <CardInner>{InnerCardContent}</CardInner>}

      {footer && (
        <CardFooter $bg={footerBg} $text_color={footerTextColor}>
          {footer}
        </CardFooter>
      )}
    </CardRoot>
  );
};

export default OrderCard;
